import React, { useMemo } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { useScenarioStore } from '../../store/scenarioStore';
import { toggleCouplingMeasure } from './couplingMeasure';

/**
 * Trennstellen whose fuse sits in the given KVS/ONS, as the Stressszenarien page sees them:
 * the state the scenario calculates with, "im Szenario" when it deviates from today, and the
 * same scenario toggle as the marker popup. Mirrors the Trennstellen rows of the live KVS
 * busbar section (`sidebar/details/shared/CouplingsSection`) so the planning panel does not
 * hide the very Abgänge that couple the zones.
 */
export const ScenarioCouplingsSection: React.FC<{ switchElementId: string }> = ({ switchElementId }) => {
  const unit = useStressTestStore(s => s.unit);
  // Subscribe to the measures so the rows re-render after a toggle (the unit is re-resolved async).
  useScenarioStore(s => s.pendingMeasures.length);

  const couplings = useMemo(
    () => (unit?.couplings ?? []).filter(c => c.switchElementId === switchElementId),
    [unit, switchElementId]
  );

  if (!unit || couplings.length === 0) return null;
  const zoneName = (id: string, name?: string) => name ?? unit.zoneNames[id] ?? id;

  return (
    <div className="bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className="bg-cyan-50 dark:bg-cyan-900/20 px-4 py-2 border-b border-gray-200 dark:border-primary-600">
        <h3 className="text-sm font-medium text-cyan-900 dark:text-cyan-200">
          Trennstellen ({couplings.length})
        </h3>
        <p className="text-[11px] text-cyan-800/80 dark:text-cyan-300/80">
          Kuppelkabel aus Nachbarzonen, die an dieser Sammelschiene gesichert sind. Schalten wirkt nur im Szenario.
        </p>
      </div>
      <div className="p-4 space-y-2">
        {couplings.map(c => {
          const overridden = c.isClosed !== c.isClosedLive;
          // The switch element is in zone B; the Kuppelkabel comes from zone A.
          const neighbour = zoneName(c.zoneAId, c.zoneAName);
          return (
            <div
              key={c.id}
              className={`p-3 rounded-lg border ${
                overridden
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
                  : 'bg-gray-50 dark:bg-primary-700 border-gray-200 dark:border-primary-600'
              }`}
              data-testid="scenario-coupling-row"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className={`w-2.5 h-2.5 rounded-full shrink-0 ${c.isClosed ? 'bg-green-500' : 'bg-orange-500'}`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-primary-100 truncate block">
                      Trennstelle → {neighbour}
                    </span>
                    <div className="text-xs text-gray-500 dark:text-primary-400 truncate">
                      Kuppelkabel aus {neighbour}{c.cableName ? ` ${c.cableName}` : ''}
                      {' · '}
                      {c.isClosed ? 'geschlossen' : 'offen'}
                      {overridden && (
                        <span className="ml-1 text-amber-700 dark:text-amber-300 font-semibold">
                          im Szenario (heute {c.isClosedLive ? 'geschlossen' : 'offen'})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleCouplingMeasure(c)}
                  className={`shrink-0 px-2.5 py-1 rounded-md text-xs font-semibold text-white ${
                    c.isClosed ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-600 hover:bg-green-700'
                  }`}
                  title="Wirkt nur im Szenario, das echte Netz wird nicht geschaltet"
                >
                  {c.isClosed ? 'im Szenario öffnen' : 'im Szenario schließen'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
